/**
 * Baralho do draft da Batalha Infinita.
 *
 * Ao fim de cada onda o jogador escolhe uma entre três cartas. É o que
 * transforma a corrida numa construção: na onda 15 o tanque é resultado de 14
 * decisões, e nenhuma corrida se parece com a anterior.
 *
 * Regras de design que este arquivo tenta sustentar:
 *  - nenhuma carta é puramente pior que outra: toda carta muda COMO se joga;
 *  - cartas de risco têm ganho grande e custo real, e nunca aparecem cedo;
 *  - o texto vem do i18n (`infinite.cards.<id>`), o ícone é a leitura primária
 *    — criança lê ícone antes de texto.
 */

import type { ModifierDelta } from "./runModifiers";
import { seededRandom, weightedSample } from "./rng";

export type CardRarity = "common" | "rare" | "epic";

export interface RunCard {
  id: string;
  icon: string;
  rarity: CardRarity;
  /** Onda mínima em que a carta pode ser oferecida. */
  minWave: number;
  /** Quantas vezes pode ser escolhida na mesma corrida. */
  maxStacks: number;
  /** Contribuição para os modificadores agregados da corrida. */
  mods: ModifierDelta;
  /** Cartas que não fazem sentido junto desta (ex.: duas de casco). */
  excludes?: string[];
}

const RARITY_WEIGHT: Record<CardRarity, number> = {
  common: 100,
  rare: 42,
  epic: 14,
};

/**
 * 24 cartas. O pool começa pequeno de propósito: é mais fácil balancear e
 * ampliar do que corrigir um baralho grande e mal ajustado.
 */
export const RUN_CARDS: readonly RunCard[] = Object.freeze([
  // ---------------- Arma ----------------
  {
    id: "twin_barrel",
    icon: "🔫",
    rarity: "common",
    minWave: 1,
    maxStacks: 3,
    mods: { shotCountBonus: 1, damageMul: 0.92 },
  },
  {
    id: "heavy_shell",
    icon: "💥",
    rarity: "common",
    minWave: 1,
    maxStacks: 4,
    mods: { damageMul: 1.18 },
  },
  {
    id: "piercing_round",
    icon: "🎯",
    rarity: "rare",
    minWave: 3,
    maxStacks: 3,
    mods: { pierce: 1 },
  },
  {
    id: "hot_powder",
    icon: "⚡",
    rarity: "common",
    minWave: 2,
    maxStacks: 3,
    mods: { bulletSpeedMul: 1.25 },
  },
  {
    id: "siege_cannon",
    icon: "🎆",
    rarity: "epic",
    minWave: 8,
    maxStacks: 1,
    mods: { damageMul: 1.55, bulletSpeedMul: 0.85 },
  },

  // ---------------- Corpo ----------------
  {
    id: "reinforced_hull",
    icon: "🛡️",
    rarity: "common",
    minWave: 1,
    maxStacks: 4,
    mods: { maxHealthBonus: 25 },
  },
  {
    id: "composite_armor",
    icon: "🧱",
    rarity: "rare",
    minWave: 4,
    maxStacks: 1,
    mods: { armor: 0.18, speedMul: 0.94 },
  },
  {
    id: "heavy_plating",
    icon: "⛨",
    rarity: "epic",
    minWave: 10,
    maxStacks: 1,
    mods: { armor: 0.32, speedMul: 0.88, maxHealthBonus: 40 },
    excludes: ["glass_cannon"],
  },
  {
    id: "field_repair",
    icon: "🔧",
    rarity: "common",
    minWave: 2,
    maxStacks: 3,
    mods: { waveHealBonus: 18 },
  },

  // ---------------- Movimento ----------------
  {
    id: "race_treads",
    icon: "💨",
    rarity: "common",
    minWave: 1,
    maxStacks: 3,
    mods: { speedMul: 1.12 },
  },
  {
    id: "amphibious_kit",
    icon: "🌊",
    rarity: "rare",
    minWave: 3,
    maxStacks: 1,
    mods: { wadingPenalty: 0.35 },
  },
  {
    id: "studded_treads",
    icon: "❄️",
    rarity: "rare",
    minWave: 5,
    maxStacks: 1,
    mods: { iceGrip: 1 },
  },
  {
    id: "heat_shield",
    icon: "🔥",
    rarity: "rare",
    minWave: 7,
    maxStacks: 2,
    mods: { hazardResist: 0.55 },
  },
  {
    id: "all_terrain",
    icon: "🛻",
    rarity: "epic",
    minWave: 12,
    maxStacks: 1,
    mods: { wadingPenalty: 0.2, iceGrip: 1, hazardResist: 0.4 },
  },

  // ---------------- Utilidade ----------------
  {
    id: "item_magnet",
    icon: "🧲",
    rarity: "common",
    minWave: 2,
    maxStacks: 2,
    mods: { magnetRadius: 8 },
  },
  {
    id: "strong_magnet",
    icon: "🧲",
    rarity: "rare",
    minWave: 8,
    maxStacks: 1,
    mods: { magnetRadius: 14 },
    excludes: ["item_magnet"],
  },
  {
    id: "scavenger",
    icon: "❤️‍🔥",
    rarity: "rare",
    minWave: 4,
    maxStacks: 3,
    mods: { lifestealPerKill: 4 },
  },
  {
    id: "second_thought",
    icon: "🔄",
    rarity: "common",
    minWave: 3,
    maxStacks: 3,
    mods: { extraRerolls: 1 },
  },

  // ---------------- Risco e recompensa ----------------
  {
    id: "glass_cannon",
    icon: "💎",
    rarity: "rare",
    minWave: 6,
    maxStacks: 1,
    mods: { damageMul: 1.6, maxHealthBonus: -35 },
    excludes: ["heavy_plating"],
  },
  {
    id: "reckless_charge",
    icon: "🏁",
    rarity: "rare",
    minWave: 6,
    maxStacks: 1,
    mods: { speedMul: 1.3, armor: 0, maxHealthBonus: -20 },
  },
  {
    id: "overload",
    icon: "☄️",
    rarity: "epic",
    minWave: 14,
    maxStacks: 1,
    mods: { damageMul: 1.35, shotCountBonus: 2, maxHealthBonus: -25 },
  },
  {
    id: "vampire_core",
    icon: "🩸",
    rarity: "epic",
    minWave: 12,
    maxStacks: 1,
    mods: { lifestealPerKill: 10, damageMul: 0.9 },
  },
  {
    id: "bulwark",
    icon: "🏰",
    rarity: "epic",
    minWave: 15,
    maxStacks: 1,
    mods: { maxHealthBonus: 90, speedMul: 0.9, waveHealBonus: 12 },
  },
  {
    id: "storm_of_lead",
    icon: "🌪️",
    rarity: "epic",
    minWave: 16,
    maxStacks: 1,
    mods: { shotCountBonus: 3, damageMul: 0.78, bulletSpeedMul: 1.15 },
  },
]);

const CARD_BY_ID = new Map(RUN_CARDS.map((card) => [card.id, card]));

export function getCard(id: string): RunCard | undefined {
  return CARD_BY_ID.get(id);
}

/** Quantas vezes cada carta já foi escolhida nesta corrida. */
export type CardCounts = Readonly<Record<string, number>>;

export function countCards(chosenIds: readonly string[]): CardCounts {
  const counts: Record<string, number> = {};
  for (const id of chosenIds) counts[id] = (counts[id] ?? 0) + 1;
  return counts;
}

/** Uma carta é oferecível se a onda liberou, ainda cabe e nada a exclui. */
export function isCardEligible(card: RunCard, wave: number, counts: CardCounts): boolean {
  if (wave < card.minWave) return false;
  if ((counts[card.id] ?? 0) >= card.maxStacks) return false;
  if (card.excludes?.some((other) => (counts[other] ?? 0) > 0)) return false;
  // Exclusão é simétrica: pegar "casco pesado" também tranca "canhão de vidro".
  for (const other of RUN_CARDS) {
    if ((counts[other.id] ?? 0) > 0 && other.excludes?.includes(card.id)) return false;
  }
  return true;
}

/**
 * Sorteia a mão do draft.
 *
 * O peso sobe com a raridade e com a onda: cartas épicas praticamente não
 * aparecem no começo e ficam comuns lá na frente, o que dá à corrida uma
 * sensação de aceleração sem mexer em nenhum número de dificuldade.
 */
export function drawCardHand(
  wave: number,
  chosenIds: readonly string[],
  seed: number,
  handSize = 3,
): RunCard[] {
  const counts = countCards(chosenIds);
  const eligible = RUN_CARDS.filter((card) => isCardEligible(card, wave, counts));
  if (eligible.length === 0) return [];

  const random = seededRandom(seed);
  const epicBoost = 1 + Math.min(2.5, Math.max(0, wave - 6) * 0.14);

  return weightedSample(
    eligible,
    (card) => {
      const base = RARITY_WEIGHT[card.rarity];
      const boosted = card.rarity === "common" ? base : base * epicBoost;
      // Uma carta já escolhida perde metade do peso por cópia: repetir é
      // possível, mas a mão continua oferecendo variedade.
      const stacks = counts[card.id] ?? 0;
      return boosted / (1 + stacks);
    },
    Math.min(handSize, eligible.length),
    random,
  );
}

/** Deltas das cartas escolhidas, na ordem em que entraram na corrida. */
export function modifierDeltasFor(chosenIds: readonly string[]): ModifierDelta[] {
  const deltas: ModifierDelta[] = [];
  for (const id of chosenIds) {
    const card = CARD_BY_ID.get(id);
    if (card) deltas.push(card.mods);
  }
  return deltas;
}
